import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Popover, PopoverContent, PopoverTrigger, cn } from "@portal/ui";
import { Bell } from "lucide-react";
import { useState } from "react";
import { markAnnouncementsRead } from "../api/client";
import { ANNOUNCEMENT_READS_KEY, useAnnouncementReads, useAnnouncements } from "../api/queries";

const fmt = new Intl.DateTimeFormat("ja-JP", { dateStyle: "medium", timeStyle: "short" });

/**
 * お知らせ(SPEC §13.4)。ナビゲーションバーのユーザーの左隣のベル(ユーザー要望 2026-10-14。以前はホームのカード)。
 * 未読があれば件数を赤い丸で出す。既読は人ごとにサーバーに記録する(どの端末でもそろう)。
 * 開くと、出ているお知らせを既読にする。開いている間は、どれが新着だったかの印を残す。
 * 本文はプレーンテキストとして改行だけ生かす(raw HTML は出さない)。
 */
export function AnnouncementsBell() {
  const list = useAnnouncements();
  const reads = useAnnouncementReads();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  // 開いたときに未読だったもの(既読にしたあとも、閉じるまで「新着」の印を残す)
  const [fresh, setFresh] = useState<ReadonlySet<string>>(new Set());
  const mark = useMutation({
    mutationFn: markAnnouncementsRead,
    onSuccess: (data) => queryClient.setQueryData(ANNOUNCEMENT_READS_KEY, data),
  });

  const rows = list.data?.announcements ?? [];
  // 既読が取れるまでは未読を数えない(全部が未読に見えて、すぐ消えるのを防ぐ)
  const readIds = new Set(reads.data?.readIds ?? []);
  const unread = reads.data ? rows.filter((a) => !readIds.has(a.id)) : [];
  const count = unread.length;

  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) return;
    setFresh(new Set(unread.map((a) => a.id)));
    if (count > 0) mark.mutate(unread.map((a) => a.id));
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          className="relative"
          aria-label={count > 0 ? `お知らせ(未読 ${count} 件)` : "お知らせ"}
          title="お知らせ"
        >
          <Bell />
          {count > 0 && (
            <span
              aria-hidden="true"
              data-testid="announcements-unread"
              className="absolute -top-0.5 -right-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-brand px-1 text-[10px] leading-none font-semibold text-white"
            >
              {count > 9 ? "9+" : count}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="flex max-h-[70vh] w-96 flex-col p-0"
        aria-label="お知らせ"
      >
        <p className="border-b px-4 py-3 text-sm font-semibold">お知らせ</p>
        {list.isError && (
          <p role="alert" className="px-4 py-3 text-sm text-destructive">
            お知らせを取得できませんでした。
          </p>
        )}
        {list.data && rows.length === 0 && (
          <p className="px-4 py-4 text-sm text-muted-foreground">お知らせはありません。</p>
        )}
        {rows.length > 0 && (
          <ul
            aria-label="お知らせの一覧"
            className="flex min-h-0 flex-col divide-y overflow-y-auto"
          >
            {rows.map((a) => (
              <li
                key={a.id}
                className={cn("flex flex-col gap-1 px-4 py-3", fresh.has(a.id) && "bg-brand/5")}
              >
                <div className="flex items-center gap-2">
                  {fresh.has(a.id) && (
                    <span
                      role="img"
                      aria-label="新着"
                      className="size-2 shrink-0 rounded-full bg-brand"
                    />
                  )}
                  {a.pinned && <Badge variant="secondary">固定</Badge>}
                  <span className="font-medium break-words">{a.title}</span>
                </div>
                <span className="text-xs text-muted-foreground">
                  {fmt.format(new Date(a.publishFrom))}
                  {a.publishUntil && ` 〜 ${fmt.format(new Date(a.publishUntil))}`}
                  {" · "}
                  {a.authorName}
                </span>
                {a.body && <p className="text-sm whitespace-pre-wrap">{a.body}</p>}
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}
